import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../../context/LangContext";
import { translations } from "../../lib/i18n";
import { TEST_IDS } from "../../constants/testIds";

const SECTIONS = ["home", "profile", "career", "goalkeepers", "methodology", "education", "references", "contact"];

const Nav = () => {
  const { lang, setLang } = useLang();
  const t = translations[lang].nav;
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid={TEST_IDS.nav.root}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "nav-scrolled" : ""}`}
    >
      <div className="rp-container flex items-center justify-between h-[72px]">
        <button
          onClick={() => go("home")}
          className="flex items-center gap-3 group"
          aria-label="Robert Prieto"
        >
          <div className="w-8 h-8 border border-white/20 flex items-center justify-center font-display font-bold text-sm">
            RP
          </div>
          <div className="hidden md:flex flex-col leading-none whitespace-nowrap">
            <span className="font-display font-semibold text-[13px] tracking-wider text-white">
              ROBERT PRIETO
            </span>
            <span className="text-[10px] tracking-[0.24em] text-[color:var(--rp-muted)] mt-1">
              GOALKEEPER COACH
            </span>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {SECTIONS.map((id) => (
            <button
              key={id}
              data-testid={TEST_IDS.nav[id]}
              onClick={() => go(id)}
              className="text-[12px] tracking-[0.18em] uppercase text-[color:var(--rp-muted)] hover:text-white transition-colors"
            >
              {t[id]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-1 text-[11px] font-medium tracking-widest">
            <button
              data-testid={TEST_IDS.nav.langEs}
              onClick={() => setLang("es")}
              className={`px-2 py-1 transition-colors ${lang === "es" ? "text-white" : "text-[color:var(--rp-muted)] hover:text-white"}`}
            >
              ES
            </button>
            <span className="text-white/20">/</span>
            <button
              data-testid={TEST_IDS.nav.langEn}
              onClick={() => setLang("en")}
              className={`px-2 py-1 transition-colors ${lang === "en" ? "text-white" : "text-[color:var(--rp-muted)] hover:text-white"}`}
            >
              EN
            </button>
          </div>
          <button
            data-testid={TEST_IDS.nav.cta}
            onClick={() => go("contact")}
            className="rp-btn rp-btn-primary hidden md:inline-flex"
          >
            {t.cta}
          </button>
          <button
            data-testid={TEST_IDS.nav.mobileMenu}
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden bg-[#0b0d0f] border-t border-white/5"
          >
            <div className="rp-container py-6 flex flex-col gap-4">
              {SECTIONS.map((id) => (
                <button
                  key={id}
                  onClick={() => go(id)}
                  className="text-left text-sm tracking-[0.18em] uppercase text-[color:var(--rp-muted)] hover:text-white"
                >
                  {t[id]}
                </button>
              ))}
              <div className="pt-4 border-t border-white/5 flex items-center gap-3">
                <button
                  onClick={() => setLang("es")}
                  className={`text-xs tracking-widest ${lang === "es" ? "text-white" : "text-[color:var(--rp-muted)]"}`}
                >
                  ES
                </button>
                <span className="text-white/20">/</span>
                <button
                  onClick={() => setLang("en")}
                  className={`text-xs tracking-widest ${lang === "en" ? "text-white" : "text-[color:var(--rp-muted)]"}`}
                >
                  EN
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
